import React, { useCallback } from 'react';
import { useStoreActions, useStoreState } from 'easy-peasy';

import { selectorUser } from '@model/User';
import { AppBanner } from '@components/AppBanner/index';
import { Trans, useTranslation } from 'react-i18next';
import { utilityGetPageState } from '@utils/get-page-state';
import { PageStateProvider } from '@components/Providers/PageStateProvider';
import { Referents } from '@components/Referents/index';
import { GTM } from '@app/gtm';
import { USER_TYPES_MAP } from '@app/constants/session';
import {
  jobProgressSelector,
  jobStoreSelector,
  selectorJobIsReverseMarketing,
} from '@model/Job/Job.selectors';
import { JOB_PROGRESS_STATES } from '@model/Job/_constants/Job.constants';
import {
  getSignedContractSelector,
  setSignedContractDataSelector,
  signedContractDataSelector,
} from '@model/Contract/Contract.selectors';
import { useDownloadFile } from '@utils/hooks/use-download-file';
import { useUserPackageVisibility } from '@components/ShareLinkModal/_hooks/use-user-package-visibility';
import { useParams } from 'react-router-dom';
import { JOB_PROGRESS_SWITCH_SEGMENT_ENTITIES } from '@components/navigation/JobProgressSwitch/JobProgressSwitch.types';
import { ProfilingPageIntro } from '@components/Molecules/ProfilingPageIntro';

const Profiling = () => {
  const { t } = useTranslation();
  const job = useStoreState(jobStoreSelector) || {};
  const isJobReverseMarketing = useStoreState(selectorJobIsReverseMarketing);
  const progress = useStoreState(jobProgressSelector);
  const { type: userType } = useStoreState(selectorUser);
  const getSignedContract = useStoreActions(getSignedContractSelector);
  const signedContractData = useStoreState(signedContractDataSelector);
  const setSignedContractData = useStoreActions(setSignedContractDataSelector);
  const userPackageVisibility = useUserPackageVisibility();
  const { entityType } = useParams();

  useDownloadFile({
    url: signedContractData?.url,
    filename: signedContractData?.filename,
    cb: () => setSignedContractData(null),
  });

  const isDownloadContractBannerVisible =
    progress &&
    userPackageVisibility.isVisible &&
    entityType === JOB_PROGRESS_SWITCH_SEGMENT_ENTITIES.JOBS;

  const pageState = utilityGetPageState({
    progress,
    currentRoute: JOB_PROGRESS_STATES.PROFILING,
  });

  const handleDownloadContractClick = useCallback(async () => {
    const eventAction = `Download contratto ${
      userType === USER_TYPES_MAP.MANAGER ? 'prospect' : 'cliente'
    }`;

    GTM.trackGTMEvent({
      action: eventAction,
      label: `${job.companyName} - ${job.id} - ${job.title}`,
    });

    GTM.trackGA4Event(eventAction, job.companyName, `${job.id} - ${job.title}`);

    await getSignedContract({ contractId: job.contractId });
  }, [job.contractId]);

  return (
    <PageStateProvider value={pageState}>
      <section data-testid="profiling-page">
        <div className="rev-o-container">
          <div className="rev-o-grid">
            {job.profilingBoardUrl || job.hasProfilingMeetingDocument ? (
              <div className="rev-u-px-16 rev-o-col-12 rev-o-col--medium-6">
                <ProfilingPageIntro />
              </div>
            ) : null}

            <div className="rev-u-px-16 rev-o-col-12 rev-o-col--medium-6">
              <Referents />
            </div>
          </div>
        </div>

        {isDownloadContractBannerVisible && (
          <div className="rev-o-container">
            <div className="rev-o-grid">
              <div className="rev-o-col-12 rev-u-px-16 rev-u-mt-16">
                <AppBanner
                  title={
                    <Trans
                      i18nKey="profile.contract.title"
                      context={isJobReverseMarketing ? 'marketing' : ''}
                    />
                  }
                  ctaLabel={t('profile.contract.download')}
                  ctaUrl={signedContractData?.url}
                  onClick={handleDownloadContractClick}
                />
              </div>
            </div>
          </div>
        )}
      </section>
    </PageStateProvider>
  );
};

export default Profiling;
